/**
 * Block B Phase 4 (2026-06-01) — Список migration runs з reverse-export.
 *
 * Дозволяє переглянути усі минулі/поточні міграції організації та
 * запустити reverse export для COMPLETED runs протягом 30 днів.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { orgs } from '@/api/catalogs';
import {
  migrationRuns,
  type MigrationRunListItem,
  type MigrationRun,
} from '@/api/migrationRuns';

export default function MigrationRunsListPage() {
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const runsList = useQuery({
    queryKey: ['migration-runs', selectedOrg],
    queryFn: () =>
      migrationRuns.list({ organization_id: selectedOrg || undefined }),
  });

  return (
    <div style={{ maxWidth: 1100, margin: '1rem auto', padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Iсторія міграцій з 1С</h2>
        <Link to="/migration/wizard">
          <button className="primary">🚀 Новий wizard міграції</button>
        </Link>
      </div>

      <div style={{ margin: '1rem 0' }}>
        <label style={{ marginRight: '0.5rem' }}>Фільтр за організацією:</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
        >
          <option value="">— Усі організації —</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      {runsList.isLoading && <p>Завантаження…</p>}

      {runsList.data && runsList.data.length === 0 && (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            background: '#f9f9f9',
            borderRadius: 8,
          }}
        >
          <p>Ще немає жодної міграції.</p>
          <Link to="/migration/wizard">
            <button className="primary">Створити першу міграцію</button>
          </Link>
        </div>
      )}

      {runsList.data && runsList.data.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem' }}>Дата створення</th>
              <th style={{ padding: '0.5rem' }}>Організація</th>
              <th style={{ padding: '0.5rem' }}>Дата міграції</th>
              <th style={{ padding: '0.5rem' }}>Профіль</th>
              <th style={{ padding: '0.5rem' }}>Джерело</th>
              <th style={{ padding: '0.5rem' }}>Статус</th>
              <th style={{ padding: '0.5rem' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {runsList.data.map((run) => (
              <RunRow
                key={run.id}
                run={run}
                orgName={
                  orgList.data?.items.find((o) => o.id === run.organization_id)
                    ?.name ?? '—'
                }
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Phase-3.5: людиночитані назви для UI badge.
const SOURCE_CONFIG_NAMES: Record<string, string> = {
  utp: '1С УТП',
  generic: '1С (інша)',
  '1c_bp_ua_2x': 'БП України',
  bas_bukh: 'BAS Бух',
  ut_ua: 'УТ / BAS УТ',
};

function RunRow({ run, orgName }: { run: MigrationRunListItem; orgName: string }) {
  // Підвантажуємо повний run щоб мати reverse_export_available_until
  const fullRun = useQuery({
    queryKey: ['migration-run', run.id],
    queryFn: () => migrationRuns.get(run.id),
    enabled: run.status === 'COMPLETED',
  });

  const reverseExport = useMutation({
    // Phase-3.6: передаємо source_config як target_format щоб backend
    // assert-нув що це той самий source (Закон 5).
    mutationFn: () => migrationRuns.reverseExport(
      run.id,
      (run.source_config as 'utp' | 'generic' | '1c_bp_ua_2x' | 'bas_bukh' | 'ut_ua' | null) || undefined,
    ),
    onSuccess: (blob) => {
      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `migration_export_${run.id}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  const sourceName = run.source_config
    ? (SOURCE_CONFIG_NAMES[run.source_config] || run.source_config)
    : null;

  return (
    <tr style={{ borderBottom: '1px solid #eee' }}>
      <td style={{ padding: '0.5rem' }}>
        {new Date(run.created_at).toLocaleString('uk')}
      </td>
      <td style={{ padding: '0.5rem' }}>{orgName}</td>
      <td style={{ padding: '0.5rem' }}>{run.migration_date}</td>
      <td style={{ padding: '0.5rem' }}>
        <code>{run.profile}</code>
      </td>
      <td style={{ padding: '0.5rem' }}>
        {sourceName ? (
          <span
            title="Визначено 1С обробкою з payload"
            style={{
              padding: '0.1rem 0.4rem',
              background: '#e8f3ff',
              border: '1px solid #06c',
              borderRadius: 4,
              color: '#06c',
              fontSize: '0.85em',
            }}
          >
            📥 {sourceName}
          </span>
        ) : (
          <small style={{ color: 'var(--muted)' }}>—</small>
        )}
      </td>
      <td style={{ padding: '0.5rem' }}>
        <StatusBadge status={run.status} percent={run.progress_percent} />
      </td>
      <td style={{ padding: '0.5rem' }}>
        {run.status === 'COMPLETED' && fullRun.data && (
          <CompletedActions
            run={fullRun.data}
            onReverseExport={() => reverseExport.mutate()}
            exportPending={reverseExport.isPending}
            exportError={reverseExport.error}
          />
        )}
        {run.status !== 'COMPLETED' && run.status !== 'FAILED' && (
          <Link to="/migration/wizard">Продовжити wizard</Link>
        )}
      </td>
    </tr>
  );
}

function StatusBadge({ status, percent }: { status: string; percent: number }) {
  const colorMap: Record<string, string> = {
    WIZARD_IN_PROGRESS: '#888',
    READY: '#06c',
    IMPORTING: '#06c',
    BULK_POSTING: '#06c',
    VALIDATING: '#06c',
    COMPLETED: '#0a8',
    FAILED: '#d33',
    ROLLED_BACK: '#888',
  };
  return (
    <span
      style={{
        padding: '0.25rem 0.5rem',
        borderRadius: 4,
        background: colorMap[status] ?? '#888',
        color: '#fff',
        fontSize: '0.85em',
      }}
    >
      {status} {percent < 100 && status !== 'WIZARD_IN_PROGRESS' ? `(${percent}%)` : ''}
    </span>
  );
}

interface CompletedActionsProps {
  run: MigrationRun;
  onReverseExport: () => void;
  exportPending: boolean;
  exportError: unknown;
}

function CompletedActions({
  run,
  onReverseExport,
  exportPending,
  exportError,
}: CompletedActionsProps) {
  const isWindowOpen =
    run.reverse_export_available_until !== null &&
    new Date(run.reverse_export_available_until) > new Date();

  const daysRemaining = run.reverse_export_available_until
    ? Math.ceil(
        (new Date(run.reverse_export_available_until).getTime() - Date.now()) /
          (24 * 60 * 60 * 1000),
      )
    : 0;

  // Phase-3.5: куди реверс? (Закон 5 — тільки туди звідки прийшло)
  const sourceName = run.source_config
    ? (SOURCE_CONFIG_NAMES[run.source_config] || run.source_config)
    : '1С';
  const exportLabel = exportPending
    ? '⏳ Генеруємо…'
    : `⬅ Експорт назад у ${sourceName}`;

  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      {isWindowOpen ? (
        <>
          <button
            className="primary"
            onClick={onReverseExport}
            disabled={exportPending}
            title={`Доступно ще ${daysRemaining} днів. Закон 5: reverse тільки у source звідки прийшло.`}
          >
            {exportLabel}
          </button>
          <small
            style={{ color: daysRemaining < 7 ? '#d33' : 'var(--muted)' }}
          >
            {daysRemaining > 0
              ? `(${daysRemaining} днів до закриття)`
              : 'останній день!'}
          </small>
        </>
      ) : (
        <small style={{ color: 'var(--muted)' }}>
          🔒 Вікно зворотньої міграції закрите
          {run.reverse_export_available_until &&
            ` (${new Date(run.reverse_export_available_until).toLocaleDateString('uk')})`}
        </small>
      )}
      {exportError !== null && exportError !== undefined && (
        <div style={{ color: '#d33', fontSize: '0.85em' }}>
          Помилка: {String(exportError)}
        </div>
      )}
    </div>
  );
}
